import { type FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useExperienceOptions } from "./useExperienceOptions";
import { type FormValues } from "../../schema";
import { Presentation } from "./Presentation";

type Props = {
  experienceUrl: string;
  jobTypeIdOptions: { label: string; value: string }[];
  onChange?: (experience: string) => void;
};

export const ExperienceSelector: FC<Props> = ({ experienceUrl, jobTypeIdOptions, onChange }) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();
  const experienceValue = watch("experience");
  const jobTypeValue = watch("jobType");
  const jobTypeName = useMemo(() => {
    const selectedOption = jobTypeIdOptions.find((option) => option.value === jobTypeValue);
    return selectedOption ? selectedOption.label : "";
  }, [jobTypeValue, jobTypeIdOptions]);
  const { experiences, isLoading, error } = useExperienceOptions(
    Number(jobTypeValue),
    experienceUrl,
  );

  const options = useMemo(
    () =>
      experiences.map(([label, value]) => ({
        label,
        value: String(value),
      })),
    [experiences],
  );

  const errorMessage = errors.experience?.message;

  const handleChange = (value: string) => {
    setValue("experience", value);
    onChange?.(experiences.find(([, v]) => String(v) === value)?.[0] || "");
  };

  return (
    <Presentation
      jobTypeName={jobTypeName}
      experienceValue={experienceValue}
      options={options}
      errorMessage={errorMessage}
      error={error}
      isLoading={isLoading}
      onChangeExperience={handleChange}
    />
  );
};
